import {useMemo, useState} from 'react';
import moment from 'moment';
import {useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Box, Card, List, ListItem, Skeleton, Stack, SxProps, Typography} from '@mui/material';
import {CareTaskComplementary} from '../../../../../backend/interfaces/care-task-complementary';
import {TaskFilterHeader} from '../../../../../components/daily-care-log/task-table/task-filter-header';
import {ScreenPaths} from '../../../../../enums';
import {translations} from '../../../../../i18n/translations';
import {useFilteredTasks} from '../../../../../hooks/filtered-tasks';
import {EntityUser} from '../../../../../backend/responses/entity-user';
import {DailyCareLogFiltersDrawer} from '../../../../../components/daily-care-log/filters-drawer';

interface GroupedShiftTabViewProps {
  tasks?: CareTaskComplementary[];
  seniors?: Map<string, EntityUser>;
}

const cardSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  height: '60px',
  width: '100%',
  padding: '5px 10px',
  gap: '10px',
  borderRadius: '5px',
  background: 'var(--dcm-white-base)',
  boxShadow: '0px 4px 16px 0px rgba(29, 41, 57, 0.10)',
};

const contentWrapperSx: SxProps = {
  width: '100%',
  flexShrink: 0,
};

const titleWrapperSx: SxProps = {
  display: 'flex',
  alignItems: 'center',
  height: '30px',
  gap: '8px',
  flexShrink: 0,
};

const titleSx: SxProps = {
  color: 'var(--dcm-gray)',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '20px',
};

const hourSx: SxProps = {
  fontWeight: 400,
};

const countSx: SxProps = {
  color: 'var(--dcm-gray)',
  textAlign: 'center',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '20px',
};

export const GroupedShiftTabView = ({tasks, seniors}: GroupedShiftTabViewProps) => {
  const {t} = useTranslation();
  const history = useHistory();
  let {pathname, search} = useLocation();
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState<boolean>(false);

  // Filter the shift care tasks according to the currently active filters
  const {filteredTasks, activeFilters, saveActiveFilters} = useFilteredTasks({tasks, seniors});

  const groupedTasks = useMemo(() => {
    if (!filteredTasks) return undefined;

    // Group tasks by common `activityTypeId` and `startDate` and `custom name`
    const groupedActivities = filteredTasks.reduce((acc, task) => {
      const group = acc.find((group) => {
        return (
          group[0].careTask.activityTypeId === task.careTask.activityTypeId &&
          moment(group[0].careTask.startsAt).isSame(task.careTask.startsAt) &&
          group[0].careTask.nameConfigurationValue === task.careTask.nameConfigurationValue
        );
      });

      if (group) {
        group.push(task);
      } else {
        acc.push([task]);
      }

      return acc;
    }, [] as CareTaskComplementary[][]);

    // Sort groups by `startDate`
    groupedActivities.sort((a, b) => {
      return moment(a[0].careTask.startsAt).diff(moment(b[0].careTask.startsAt));
    });

    return groupedActivities;
  }, [filteredTasks]);

  const handleCardClick = (tasks: CareTaskComplementary[]) => {
    const groupTime = moment(tasks[0].careTask.startsAt).format('HH:mm');
    const activityTypeId = tasks[0].careTask.activityTypeId;
    const groupName = tasks[0].careTask.nameConfigurationValue;

    let url =
      `${pathname}${ScreenPaths.DailyCareLog_Grouped_Tasks}${search}&groupTime=${groupTime}`.replace(
        ':activityTypeId',
        activityTypeId
      );

    if (groupName) {
      url += `&groupName=${encodeURIComponent(groupName)}`;
    }

    history.push(url, {tasks});
  };

  return (
    <Stack gap={1}>
      <DailyCareLogFiltersDrawer
        activeFilters={activeFilters}
        onFiltersSave={saveActiveFilters}
        open={filtersDrawerOpen}
        onDrawerClose={() => setFiltersDrawerOpen(false)}
      />
      {/**
       * Header
       */}
      <TaskFilterHeader onFilterClick={() => setFiltersDrawerOpen(true)} />

      {/**
       * List grouped tasks
       */}
      <List sx={{paddingBottom: 0, gap: 1, display: 'flex', flexDirection: 'column'}}>
        {groupedTasks && groupedTasks.length === 0 && (
          <h3 className="title">{t(translations.daily_care_log.task_table.empty)}</h3>
        )}

        {groupedTasks
          ? groupedTasks.map((grouped, idx) => {
              const ctc = grouped[0]; // ctc - CareTaskComplementary
              const total = grouped.length;
              const key = ctc.careTask.id + `_${idx}`;
              const date = moment(ctc.careTask.startsAt).format('LT');

              return (
                <ListItem key={`task_card_${key}`} sx={{width: '100%', padding: 0}}>
                  <Card sx={cardSx} onClick={() => handleCardClick(grouped)}>
                    <Stack flexDirection="column" gap="2px" sx={contentWrapperSx}>
                      <Box sx={titleWrapperSx}>
                        <Typography sx={titleSx} component="h5" noWrap>
                          <Typography sx={hourSx} component="span">
                            {date}
                          </Typography>{' '}
                          - {ctc.careTask.nameConfigurationValue ?? ctc.activityTypeName}
                        </Typography>
                      </Box>
                      <Stack alignItems="flex-end">
                        <Typography sx={countSx} component="h5">
                          {total}{' '}
                          {(
                            t(translations.daily_care_log.shift_details.tabs.grouped.task) as string
                          ).toLowerCase()}
                          {total > 1 ? 's' : ''}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Card>
                </ListItem>
              );
            })
          : new Array(12)
              .fill(1)
              .map((_item: number, index: number) => (
                <Skeleton key={`Grouped_task_skeleton_${index}`} height="50px" width="100%" />
              ))}
      </List>
    </Stack>
  );
};
